import * as THREE from 'three'

// 3D entity meshes (replacing the old billboarded pixel-art) — a cozy, chunky, rounded look (Pokopia-style):
// creatures are routed to one of a few BODY ARCHETYPES (round blob / little flyer / big brute / hooded caster), each a
// merged mesh with two baked dark EYES so every alien has a face. One instanced draw call per archetype; per-instance
// colour + a facing rotation so the eyes look at the player. Lit by the warm rig + casts a soft shadow.

const _m = new THREE.Matrix4()
const _q = new THREE.Quaternion()
const _yAxis = new THREE.Vector3(0, 1, 0)
const _pos = new THREE.Vector3()
const _scl = new THREE.Vector3()
const _col = new THREE.Color()
const _base = new THREE.Color()

/**
 * Merge several pre-transformed geometries into ONE non-indexed BufferGeometry, baking a flat per-part vertex colour.
 * Keeps body parts (body/eyes/horns/hat) in a single instanceable geometry without an examples/jsm dependency.
 */
export function mergeGeoms(parts: { geo: THREE.BufferGeometry; color: number }[]): THREE.BufferGeometry {
  let vtot = 0
  const flats = parts.map((p) => {
    const g = p.geo.index ? p.geo.toNonIndexed() : p.geo
    const n = g.getAttribute('position').count
    vtot += n
    return { g, n, color: new THREE.Color(p.color) }
  })
  const pos = new Float32Array(vtot * 3)
  const nor = new Float32Array(vtot * 3)
  const col = new Float32Array(vtot * 3)
  let o = 0
  for (const f of flats) {
    const gp = f.g.getAttribute('position') as THREE.BufferAttribute
    const gn = f.g.getAttribute('normal') as THREE.BufferAttribute
    for (let i = 0; i < f.n; i++) {
      pos[o * 3] = gp.getX(i); pos[o * 3 + 1] = gp.getY(i); pos[o * 3 + 2] = gp.getZ(i)
      nor[o * 3] = gn.getX(i); nor[o * 3 + 1] = gn.getY(i); nor[o * 3 + 2] = gn.getZ(i)
      col[o * 3] = f.color.r; col[o * 3 + 1] = f.color.g; col[o * 3 + 2] = f.color.b
      o++
    }
  }
  const out = new THREE.BufferGeometry()
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3))
  out.setAttribute('normal', new THREE.BufferAttribute(nor, 3))
  out.setAttribute('color', new THREE.BufferAttribute(col, 3))
  return out
}

const EYE = 0x14121c // near-black eyes (kept dark even after the body tint multiplies in)
const WHITE = 0xffffff // body verts = white so the per-instance colour shows through unchanged

/** Two eyes on the +X front of the body at height `y`, spread by `sp`, size `r`. */
function eyes(y: number, sp: number, r: number, fwd: number): { geo: THREE.BufferGeometry; color: number }[] {
  return [-sp, sp].map((dz) => {
    const g = new THREE.SphereGeometry(r, 7, 6)
    g.translate(fwd, y, dz)
    return { geo: g, color: EYE }
  })
}

function blobGeo(): THREE.BufferGeometry {
  const body = new THREE.SphereGeometry(0.5, 16, 13); body.scale(1.05, 0.92, 1.05); body.translate(0, 0.46, 0)
  return mergeGeoms([{ geo: body, color: WHITE }, ...eyes(0.56, 0.17, 0.09, 0.44)])
}
function flyerGeo(): THREE.BufferGeometry {
  // small, elongated forward, sits a touch off the ground (hover read)
  const body = new THREE.SphereGeometry(0.42, 14, 11); body.scale(1.25, 0.9, 0.95); body.translate(0, 0.5, 0)
  return mergeGeoms([{ geo: body, color: WHITE }, ...eyes(0.6, 0.14, 0.08, 0.5)])
}
function bruteGeo(): THREE.BufferGeometry {
  const body = new THREE.SphereGeometry(0.5, 16, 13); body.scale(1.15, 1.18, 1.15); body.translate(0, 0.62, 0)
  const hornL = new THREE.ConeGeometry(0.12, 0.34, 7); hornL.translate(0.16, 1.22, -0.2)
  const hornR = new THREE.ConeGeometry(0.12, 0.34, 7); hornR.translate(0.16, 1.22, 0.2)
  return mergeGeoms([{ geo: body, color: WHITE }, { geo: hornL, color: 0xcfcfcf }, { geo: hornR, color: 0xcfcfcf }, ...eyes(0.74, 0.2, 0.1, 0.52)])
}
function casterGeo(): THREE.BufferGeometry {
  const body = new THREE.SphereGeometry(0.46, 15, 12); body.scale(0.95, 1.0, 0.95); body.translate(0, 0.5, 0)
  const hat = new THREE.ConeGeometry(0.4, 0.6, 12); hat.translate(0, 1.18, 0)
  return mergeGeoms([{ geo: body, color: WHITE }, { geo: hat, color: 0xdddddd }, ...eyes(0.56, 0.15, 0.085, 0.46)])
}

export type Archetype = 'blob' | 'flyer' | 'brute' | 'caster'
const GEO_BUILDERS: Record<Archetype, () => THREE.BufferGeometry> = { blob: blobGeo, flyer: flyerGeo, brute: bruteGeo, caster: casterGeo }

/** A single-draw-call batch of one creature archetype. begin() → add(...) per visible enemy of that type → end(). */
export class CreatureBatch {
  readonly mesh: THREE.InstancedMesh
  private count = 0
  private readonly cap: number

  constructor(archetype: Archetype, capacity: number) {
    this.cap = capacity
    const geom = GEO_BUILDERS[archetype]()
    const mat = new THREE.MeshStandardMaterial({ roughness: 0.82, metalness: 0.0, vertexColors: true })
    this.mesh = new THREE.InstancedMesh(geom, mat, capacity)
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    this.mesh.castShadow = true
    this.mesh.receiveShadow = false
    this.mesh.frustumCulled = false
    this.mesh.count = 0
    this.mesh.setColorAt(0, _col.setHex(0xffffff))
  }

  begin(): void { this.count = 0 }

  /**
   * Queue one creature. (x,y,z) = feet position, `diameter` = body size, `facing` rotates it so the eyes face the
   * player, `baseHex` its colour, (rMul,gMul,bMul) the state tint multipliers (flash/freeze/elite…).
   */
  add(x: number, y: number, z: number, diameter: number, facing: number, baseHex: number, rMul: number, gMul: number, bMul: number): void {
    if (this.count >= this.cap) return
    const i = this.count++
    _pos.set(x, y, z)
    _q.setFromAxisAngle(_yAxis, -facing)
    _scl.set(diameter, diameter, diameter)
    _m.compose(_pos, _q, _scl)
    this.mesh.setMatrixAt(i, _m)
    _base.setHex(baseHex)
    _col.setRGB(Math.min(1, _base.r * rMul), Math.min(1, _base.g * gMul), Math.min(1, _base.b * bMul))
    this.mesh.setColorAt(i, _col)
  }

  end(): void {
    this.mesh.count = this.count
    this.mesh.instanceMatrix.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true
  }
}

/**
 * The player hero as a rounded 3D character: chunky body + big soft head with eyes + little feet (Pokopia's
 * "big-headed, soft, deformed" proportions), tinted by the character colour. Single Group; faces travel dir.
 */
export class PlayerMesh {
  readonly group: THREE.Group
  private mats: THREE.MeshStandardMaterial[] = []

  constructor() {
    this.group = new THREE.Group()
    const skin = () => { const m = new THREE.MeshStandardMaterial({ roughness: 0.78, metalness: 0.0 }); this.mats.push(m); return m }
    const bodyGeom = new THREE.SphereGeometry(0.44, 20, 16); bodyGeom.scale(1, 1.02, 1)
    const body = new THREE.Mesh(bodyGeom, skin()); body.position.y = 0.52; body.castShadow = true
    const headGeom = new THREE.SphereGeometry(0.37, 20, 16)
    const head = new THREE.Mesh(headGeom, skin()); head.position.y = 1.14; head.castShadow = true
    this.group.add(body, head)
    // little feet (darker, fixed) so it reads as a character, not a snowman
    const footMat = new THREE.MeshStandardMaterial({ color: 0x2a2630, roughness: 0.7 })
    for (const dz of [-0.2, 0.2]) { const f = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8), footMat); f.position.set(0.05, 0.13, dz); f.scale.set(1.2, 0.7, 1); f.castShadow = true; this.group.add(f) }
    // eyes on the front (+x)
    const eyeMat = new THREE.MeshStandardMaterial({ color: 0x14121c, roughness: 0.5 })
    for (const dz of [-0.15, 0.15]) { const e = new THREE.Mesh(new THREE.SphereGeometry(0.075, 10, 9), eyeMat); e.position.set(0.31, 1.2, dz); this.group.add(e) }
    // a tiny highlight cheek dot pair (white) for a friendly read
    const shineMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4 })
    for (const dz of [-0.15, 0.15]) { const s = new THREE.Mesh(new THREE.SphereGeometry(0.028, 6, 6), shineMat); s.position.set(0.37, 1.24, dz - 0.02); this.group.add(s) }
  }

  /** Place + colour the hero. `facing` is the travel angle; `bright` flashes it on hit. */
  update(x: number, y: number, z: number, facing: number, colorHex: number, bright: number): void {
    this.group.position.set(x, y, z)
    this.group.rotation.y = -facing
    _base.setHex(colorHex)
    _col.setRGB(Math.min(1, _base.r * bright), Math.min(1, _base.g * bright), Math.min(1, _base.b * bright))
    const e = Math.max(0, bright - 1) * 0.5
    for (const m of this.mats) { m.color.copy(_col); m.emissive.setRGB(e, e * 0.3, e * 0.3) }
  }
}
